import React from "react";
import { Calendar, CheckCircle, FileText, Plus } from "lucide-react";
import { ICONS } from "./constants";

export default function TeacherDetailTimelineSection({
  attendanceLogs,
  teacher,
  userProfile,
  visitLogs,
}) {
  const currentMemberRole = userProfile?.memberRole;
  const myUid = userProfile?.uid || userProfile?.id;
  const isLinkedUser = myUid && teacher.linkedUserId && String(teacher.linkedUserId) === String(myUid);
  const isMyProfile = isLinkedUser;
  const shouldMask = currentMemberRole === "teacher" && !isMyProfile;

  const header = (
    <div className="flex justify-between items-end mb-4 px-1">
      <h3 className="text-xl font-bold text-gray-900">사역 타임라인</h3>
    </div>
  );

  if (shouldMask) {
    return (
      <div className="w-full space-y-1 mb-8">
        <div className="w-full">
          {header}
          <div className="bg-[#F9FAFB]/50 rounded-[2rem] p-12 border border-gray-100 flex flex-col items-center justify-center gap-4 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-2 blur-[2px]">
              <span className="text-2xl">🔒</span>
            </div>
            <div className="space-y-1">
              <span className="block text-gray-400 font-bold text-lg blur-[4px] select-none bg-gray-100 rounded px-4 py-1 mx-auto w-fit">2024년 12월 25일 활동 기록</span>
              <span className="block text-gray-300 font-medium text-sm blur-[3px] select-none">상세 내용이 가려져 있습니다</span>
            </div>
            <p className="text-xs text-gray-400 font-bold mt-4 bg-white/50 px-4 py-2 rounded-xl border border-gray-100">활동 기록은 본인 및 관리자만 확인할 수 있습니다.</p>
          </div>
        </div>
      </div>
    );
  }

  const history = [];

  if (visitLogs) {
    visitLogs
      .filter((log) => log.teacherId === teacher.id || log.studentId === teacher.id)
      .forEach((log) => {
        history.push({
          type: "visit",
          date: new Date(log.date),
          title:
            log.type === "상담"
              ? "마음을 나눴던 소중한 시간"
              : log.type === "기도"
                ? "함께 마음 모아 기도했습니다"
                : "삶의 자리에서 함께했습니다",
          content: log.content,
          icon:
            log.type === "상담" ? (
              <FileText size={16} />
            ) : log.type === "기도" ? (
              <img src={ICONS.prayer} width={16} alt="pray" />
            ) : (
              <CheckCircle size={16} />
            ),
          color:
            log.type === "상담"
              ? "bg-blue-500 shadow-blue-200"
              : log.type === "기도"
                ? "bg-indigo-500 shadow-indigo-200"
                : "bg-[#354F38] shadow-emerald-200",
          raw: log,
        });
      });
  }

  if (attendanceLogs) {
    const recentAttendance = attendanceLogs
      .filter((log) => log[teacher.id])
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5);

    recentAttendance.forEach((log) => {
      const status = log[teacher.id];
      if (status === "present" || status === "late") {
        history.push({
          type: "attendance",
          date: new Date(log.date),
          title: status === "present" ? "예배의 자리를 지켰습니다" : "조금 늦었지만, 함께해서 기쁩니다",
          icon: <Calendar size={16} />,
          color: status === "present" ? "bg-emerald-500 shadow-emerald-200" : "bg-amber-500 shadow-amber-200",
        });
      }
    });
  }

  if (teacher.createdAt) {
    history.push({
      type: "system",
      date: teacher.createdAt.toDate ? teacher.createdAt.toDate() : new Date(teacher.createdAt),
      title: "The STAFF 교사 등록",
      content: "우리 공동체와 함께한 시작일이에요! 🌱",
      icon: <Plus size={16} />,
      color: "bg-gray-800 shadow-gray-200",
    });
  }

  const sortedHistory = history.sort((a, b) => b.date - a.date);

  return (
    <div className="w-full space-y-1 mb-8">
      <div className="w-full">
        {header}
        <div className="bg-[#F9FAFB]/50 rounded-[2rem] p-6 border border-gray-100 relative overflow-hidden">
          <div className="absolute left-10 top-10 bottom-10 w-0.5 bg-gradient-to-b from-[#354F38]/20 via-gray-100 to-transparent"></div>

          <div className="space-y-8 relative">
            {sortedHistory.length === 0 ? (
              <div className="py-12 flex flex-col items-center gap-4 opacity-50">
                <img src={ICONS.memo} width={40} className="grayscale" alt="empty" />
                <p className="text-sm font-bold text-gray-400">활동 기록이 비어있습니다</p>
              </div>
            ) : (
              sortedHistory.map((item, idx) => (
                <div key={idx} className="relative pl-12 group cursor-default transition-all duration-300">
                  <div className={`absolute left-0 top-1 w-8 h-8 rounded-2xl ${item.color} shadow-lg z-10 flex items-center justify-center text-white transform group-hover:scale-110 transition-transform duration-300 ${idx === 0 ? "animate-pulse" : ""}`}>
                    {item.icon}
                  </div>

                  <div className="bg-white/40 backdrop-blur-sm rounded-2xl p-4 border border-white hover:bg-white hover:shadow-xl hover:shadow-gray-200/50 transition-all duration-500">
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-black text-[#354F38]/40 uppercase tracking-widest">{item.date.toLocaleDateString("ko-KR", { year: "2-digit", month: "long", day: "numeric" })}</span>
                      <h4 className="text-sm font-black text-gray-800">{item.title}</h4>
                      {item.content && (
                        <p className="text-xs text-gray-500 font-medium leading-relaxed mt-1 break-keep p-3 bg-gray-50/50 rounded-xl border border-gray-100 line-clamp-3 group-hover:line-clamp-none transition-all duration-500">
                          {item.content}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
